import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as actions from '../../../actions';
import PropTypes from 'prop-types';

import styled from 'styled-components';
import { Avatar, Button, Card, Icon, Input, message } from 'antd';
import Recaptcha from 'react-google-invisible-recaptcha';

import localFakeBlurCardsRateJSON from '../../../data/fake-blur-cards-rates.json';
import { noOfBlurCards } from '../../../util/noOfBlurCards';
import {
  RateGateStatus,
  RATE_GATE_ERROR_COPY,
} from '../../../util/rateGate';
import {
  G_RECAPTCHA_SITE_KEY,
  RATE_GATE_LOGIN_URL,
} from '../../../envVariables';
import { ReCaptchaPrivacyTermsToolTip } from '../../common/ReCaptchaPrivacyTermsToolTip';
import {
  color,
  radius,
  shadow,
  transition,
  tapTarget,
} from '../../../styles/designTokens';

// ── copy (signed-off sheet — notes/public-rate-gate-mockup.html §5) ──────────
const COPY = Object.freeze({
  unlockTitle: 'See live rates for this lane',
  unlockBody: 'Enter your work email to unlock today’s pricing.',
  unlockButton: 'Unlock rates',
  gateTitle: 'Your company already works with Prime Freight',
  gateBody:
    'Log in to see your company’s rates for this lane — they may be better than public pricing. Need access? Ask your account admin, or request it below.',
  login: 'Log in',
  requestAccess: 'Request access',
  requestSent: 'Request sent',
  error: RATE_GATE_ERROR_COPY,
  invalidEmail: 'Please enter a valid work email.',
  accessToast: 'Request sent — our team will be in touch shortly.',
  returningTitle: 'Unlocking your rates…',
  returningBody: 'One moment — we’re applying your saved details to this search.',
});

// If the silent re-verify for a returning lead hasn't revealed the cards within
// this window (captcha slow / blocked / token failure), fall back to the manual
// unlock form so a known visitor can never get stuck behind a spinner.
const RETURNING_VISITOR_FALLBACK_MS = 7000;

// Basic email-shape check, no regex (avoids the super-linear backtracking Sonar
// flags + matches the house "avoid regex" rule). Enough for a gate input: exactly
// one "@" with a non-empty local part, a dotted domain, and no whitespace.
const isValidEmail = (email) => {
  if (typeof email !== 'string') return false;
  const value = email.trim();
  if (!value || value.includes(' ') || value.includes('\t') || value.includes('\n')) {
    return false;
  }
  const at = value.indexOf('@');
  if (at <= 0 || at !== value.lastIndexOf('@')) return false;
  const domain = value.slice(at + 1);
  const dot = domain.indexOf('.');
  return dot > 0 && dot < domain.length - 1;
};

// ── styled (tokens from styles/designTokens) ─────────────────────────────────
const StyledCard = styled(Card)`
  -webkit-filter: blur(5px);
  -moz-filter: blur(5px);
  -o-filter: blur(5px);
  -ms-filter: blur(5px);
  filter: blur(5px);
`;

const GateCard = styled.div`
  background: #fff;
  border: 1px solid ${color.line};
  border-radius: ${radius.card};
  box-shadow: ${shadow.overlay};
  padding: 22px 26px;
  width: min(400px, 94%);
  transition: ${transition};
  @media (max-width: 480px) {
    padding: 18px 16px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const GateTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: ${color.navy};
`;

const GateBody = styled.p`
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.55;
  color: ${color.ink2};
`;

const FieldRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const EmailInput = styled(Input)`
  && {
    flex: 1 1 180px;
    height: ${tapTarget};
    border-radius: ${radius.base};
    border-color: ${color.line};
    font-size: 14px;
    color: ${color.ink};
    transition: ${transition};
  }
  &&:hover {
    border-color: ${color.primary};
  }
  &&:focus {
    outline: none;
    border-color: ${color.primary};
    box-shadow: 0 0 0 3px rgba(30, 148, 241, 0.18);
  }
  && {
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
`;

const PrimaryBtn = styled(Button)`
  && {
    height: ${tapTarget};
    min-width: 128px;
    padding: 0 18px;
    border-radius: ${radius.base};
    background: ${color.primary};
    border-color: ${color.primary};
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    transition: ${transition};
  }
  &&:hover,
  &&:focus {
    background: #3aa3f5;
    border-color: #3aa3f5;
    color: #fff;
    box-shadow: 0 0 0 3px rgba(30, 148, 241, 0.18);
  }
  &&[disabled],
  &&.ant-btn-loading {
    opacity: 0.65;
  }
  && {
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
`;

const GhostBtn = styled(Button)`
  && {
    height: ${tapTarget};
    min-width: 128px;
    padding: 0 18px;
    border-radius: ${radius.base};
    background: #fff;
    border-color: ${color.primary};
    color: ${color.primary};
    font-size: 14px;
    font-weight: 600;
    transition: ${transition};
  }
  &&:hover,
  &&:focus {
    background: ${color.primarySoft};
    color: ${color.primary};
    border-color: ${color.primary};
    box-shadow: 0 0 0 3px rgba(30, 148, 241, 0.18);
  }
  &&[disabled] {
    background: #fff;
    border-color: ${color.line};
    color: ${color.ink2};
    opacity: 0.85;
  }
  && {
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
`;

const BtnRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  & > * {
    flex: 1 1 128px;
  }
`;

const ErrorText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 10px;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${color.danger};
`;

const HintRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 11px;
  color: ${color.ink2};
`;

const HintBadge = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 3px;
  background: ${color.primarySoft};
  border: 1px solid ${color.line};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${color.primary};
  font-size: 11px;
  font-weight: 700;
`;

const cardsRef = React.createRef();
const emailFormRef = React.createRef();

/**
 * @component
 * @category Search Page
 * @name BlurCards
 * @description The public rate gate: blurred rate cards behind a centered
 * unlock card. Email + invisible reCAPTCHA → pf-comms /rate-gate/unlock
 * (rate_gate_actions.unlockRates). States (state.route.rateGate.status):
 * idle → verifying → unlocked (overlay dismissed) | gated (hard-gate card:
 * log in / request access) | error (inline, retryable).
 */
class BlurCards extends Component {
  state = {
    inputEmail: '',
    topMargin: 0,
    localError: null,
    requestingAccess: false,
    accessRequested: false,
    // Returning lead: show the "unlocking…" spinner (not the email form) while
    // the silent re-verify runs. Flips true if that verify doesn't reveal the
    // cards in time, so we fall back to the manual form instead of hanging.
    autoVerifyFailed: false,
  };

  // one silent re-verify per mount for returning visitors (persisted email)
  autoAttempted = false;
  pendingEmail = null;
  fallbackTimer = null;

  componentDidMount = () => {
    const { userEmailBlurCards } = this.props;

    // Prefill the persisted email (redux-persist). The actual unlock now needs
    // a fresh captcha token, so the silent re-verify runs from onCaptchaLoaded.
    // For a returning lead the form is hidden behind the "unlocking…" spinner
    // (renderReturningVisitor); arm a fallback so a slow/blocked captcha can't
    // strand them there.
    if (isValidEmail(userEmailBlurCards)) {
      this.setState({ inputEmail: userEmailBlurCards });
      this.fallbackTimer = setTimeout(
        () => this.setState({ autoVerifyFailed: true }),
        RETURNING_VISITOR_FALLBACK_MS
      );
    }

    // sets top margin
    this.setsTopMargin();
  };

  componentWillUnmount = () => {
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
  };

  // A returning visitor we can silently re-unlock for (no re-prompt): a valid
  // persisted lead email and the silent verify hasn't given up yet.
  isReturningLead = () =>
    isValidEmail(this.props.userEmailBlurCards) && !this.state.autoVerifyFailed;

  componentWillReceiveProps = (nextProps) => {
    const { parentScrollbarPosition, updatePickupDeliveryErsLine } = this.props;

    // set top margin when parent website scroll position changes
    if (parentScrollbarPosition !== nextProps.parentScrollbarPosition) {
      this.setsTopMargin();
      return true;
    }

    // for uuid request, after removing old rates and adding new rates with unique id,
    // it sets top margin
    if (updatePickupDeliveryErsLine !== nextProps.updatePickupDeliveryErsLine) {
      this.setsTopMargin();
      return true;
    }

    return false;
  };

  // ── unlock flow ────────────────────────────────────────────────────────────

  /**
   * @description Validate the email locally, then execute the invisible
   * reCAPTCHA; the resolved token submits through onCaptchaResolved.
   */
  onSubmit = () => {
    const email = (this.state.inputEmail || '').trim();
    if (!isValidEmail(email)) {
      this.setState({ localError: COPY.invalidEmail });
      return;
    }
    this.setState({ localError: null });
    this.executeCaptcha(email);
  };

  executeCaptcha = async (email) => {
    this.pendingEmail = email;
    if (this.recaptcha?.execute) {
      try {
        // execute()'s own resolved value IS the token — this is the primary
        // path (verified reliable). onResolved (the widget's window-scoped
        // legacy callback) is wired as a defensive fallback only: some
        // reCAPTCHA account/project states never invoke it even though the
        // execute() promise resolves fine, so we don't depend on it.
        const token = await this.recaptcha.execute();
        if (this.pendingEmail === email) {
          this.dispatchUnlock(email, token || '');
          if (this.recaptcha.reset) this.recaptcha.reset();
        }
        return;
      } catch (error) {
        console.log('g reCaptcha error:', error);
      }
    }
    // Captcha unavailable (script blocked / key missing) — submit anyway and
    // let the server-side verification decide.
    this.dispatchUnlock(email, '');
  };

  onCaptchaResolved = () => {
    // Defensive fallback only — see executeCaptcha. this.pendingEmail is
    // already null once the promise path has dispatched, so this becomes a
    // safe no-op if the legacy callback fires (or double-fires) afterward.
    if (!this.pendingEmail) return;
    const token = this.recaptcha?.getResponse?.() || '';
    const email = this.pendingEmail;
    this.dispatchUnlock(email, token);
    // reset so a retry can execute a fresh challenge
    if (this.recaptcha?.reset) {
      this.recaptcha.reset();
    }
  };

  onCaptchaLoaded = () => {
    // Returning visitor: silently re-verify the persisted email once, so the
    // cards unlock without retyping (matches the pre-gate behavior, now with a
    // real captcha + gate check behind it).
    const { userEmailBlurCards, rateGate } = this.props;
    const status = rateGate?.status || RateGateStatus.IDLE;
    const canAuto =
      status === RateGateStatus.IDLE || status === RateGateStatus.UNLOCKED;
    if (!this.autoAttempted && canAuto && isValidEmail(userEmailBlurCards)) {
      this.autoAttempted = true;
      this.executeCaptcha(userEmailBlurCards.trim());
    }
  };

  dispatchUnlock = (email, captchaToken) => {
    this.pendingEmail = null;
    this.lastSubmittedEmail = email;
    this.props.unlockRates({ email, captchaToken });
  };

  onInputChange = (e) => {
    this.setState({ inputEmail: e.target.value, localError: null });
    const { rateGate } = this.props;
    if (rateGate?.status === RateGateStatus.ERROR) {
      this.props.resetRateGateError();
    }
  };

  // ── hard gate ──────────────────────────────────────────────────────────────

  onLogin = () => {
    const { rateGate } = this.props;
    const url = rateGate?.loginUrl || RATE_GATE_LOGIN_URL;
    window.open(url, '_blank', 'noopener');
  };

  onRequestAccess = async () => {
    const email = (
      this.lastSubmittedEmail ||
      this.state.inputEmail ||
      this.props.userEmailBlurCards ||
      ''
    ).trim();
    if (!isValidEmail(email)) {
      this.setState({ localError: COPY.invalidEmail });
      return;
    }
    this.setState({ requestingAccess: true });
    const res = await this.props.requestRateGateAccess(email);
    const ok = !!res?.ok;
    this.setState({ requestingAccess: false, accessRequested: ok });
    if (ok) {
      message.success(COPY.accessToast);
    } else {
      message.error(COPY.error);
    }
  };

  /**
   * @description It sets the top margin to popup blur cards component.
   */
  setsTopMargin = () => {
    const { parentScrollbarPosition } = this.props;

    const cardsHt = (cardsRef.current || {}).clientHeight;
    const emailFormHt = emailFormRef.current?.clientHeight || 160;

    //one card height is 264px, marginBottom is 15px → one card slot = 279px
    const minTopMargin = Math.max(0, (279 - emailFormHt) / 2);
    const maxTopMargin = Math.max(minTopMargin, cardsHt - emailFormHt);

    let totalTopMargin = parentScrollbarPosition;

    totalTopMargin =
      totalTopMargin < minTopMargin ? minTopMargin : totalTopMargin;

    totalTopMargin =
      totalTopMargin >= maxTopMargin ? maxTopMargin : totalTopMargin;

    this.setState({ topMargin: totalTopMargin });
  };

  // ── render pieces ──────────────────────────────────────────────────────────

  renderUnlockForm = (status) => {
    const { inputEmail, localError } = this.state;
    const verifying = status === RateGateStatus.VERIFYING;
    const { rateGate } = this.props;
    const storeError =
      status === RateGateStatus.ERROR
        ? rateGate?.errorMessage || COPY.error
        : null;
    const errorMessage = localError || storeError;

    return (
      <div>
        <GateTitle>{COPY.unlockTitle}</GateTitle>
        <GateBody>{COPY.unlockBody}</GateBody>
        <FieldRow>
          <EmailInput
            type="email"
            inputMode="email"
            autoComplete="email"
            aria-label="Work email"
            placeholder="you@company.com"
            value={inputEmail || ''}
            disabled={verifying}
            onChange={this.onInputChange}
            onPressEnter={this.onSubmit}
          />
          <PrimaryBtn loading={verifying} onClick={this.onSubmit}>
            {COPY.unlockButton}
          </PrimaryBtn>
        </FieldRow>
        {errorMessage && (
          <ErrorText role="alert">
            <Icon
              type="exclamation-circle"
              style={{ fontSize: '14px', marginTop: '2px' }}
            />
            <span>{errorMessage}</span>
          </ErrorText>
        )}
        <HintRow>
          <HintBadge>✓</HintBadge>
          <span>Protected by reCAPTCHA</span>
          <ReCaptchaPrivacyTermsToolTip />
        </HintRow>
      </div>
    );
  };

  // Returning lead: the silent re-verify (onCaptchaLoaded → executeCaptcha →
  // unlock) is in flight, so we show a brief "unlocking…" spinner instead of
  // re-prompting for the email they already gave. The unlock re-applies the
  // discount and re-associates this search to their lead (same sessionUid).
  renderReturningVisitor = () => (
    <div>
      <GateTitle>{COPY.returningTitle}</GateTitle>
      <GateBody>{COPY.returningBody}</GateBody>
      <div style={{ padding: '4px 0 2px' }}>
        <Icon type="loading" style={{ fontSize: '20px' }} />
      </div>
    </div>
  );

  // Which overlay panel to show: hard gate (existing account) → returning-lead
  // spinner (known email, silent re-verify in flight) → the email form.
  renderGatePanel = (status, isGated) => {
    if (isGated) return this.renderHardGate();
    if (this.isReturningLead() && status !== RateGateStatus.ERROR) {
      return this.renderReturningVisitor();
    }
    return this.renderUnlockForm(status);
  };

  renderHardGate = () => {
    const { requestingAccess, accessRequested } = this.state;
    return (
      <div>
        <GateTitle>{COPY.gateTitle}</GateTitle>
        <GateBody>{COPY.gateBody}</GateBody>
        <BtnRow>
          <PrimaryBtn onClick={this.onLogin}>{COPY.login}</PrimaryBtn>
          <GhostBtn
            loading={requestingAccess}
            disabled={accessRequested}
            onClick={this.onRequestAccess}
          >
            {accessRequested ? COPY.requestSent : COPY.requestAccess}
          </GhostBtn>
        </BtnRow>
      </div>
    );
  };

  render() {
    const { topMargin } = this.state;
    const { seaFCLJSON, seaLCLJSON, airJSON, rateGate } = this.props;

    const status = rateGate?.status || RateGateStatus.IDLE;
    const isGated = status === RateGateStatus.GATED;

    const blurCardObj = localFakeBlurCardsRateJSON;

    const noOfResultBlurCards = noOfBlurCards(seaFCLJSON, seaLCLJSON, airJSON);

    return (
      <div
        ref={cardsRef}
        className="testR1"
        style={{
          position: 'relative',

          height: 'auto',
          ...(window.innerWidth >= 993
            ? {
                width: '675px',
              }
            : {
                maxWidth: '675px',
                width: '100%',
              }),
        }}
      >
        {/* array of blurry cards*/}
        {Array(noOfResultBlurCards)
          .fill()
          .map((_, index) => {
            return (
              <StyledCard
                key={index}
                style={{
                  // height: '100%',
                  width: '100%',
                  maxWidth: '675px',
                  height: '264px',
                  margin: '0px 0px 15px 0px',
                  padding: '0px',
                  borderRadius: '5px',
                  boxShadow: '0 1px 5px rgba(0, 0, 0, 0.15)',
                  background: 'white',
                  marginBottom: '15px',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'stretch',
                  }}
                >
                  {/* left side of card */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-between',
                      height: '100%',
                      maxWidth: '451px',
                      width: '100%',
                      border: '1px solid grey',
                    }}
                    className="testR1"
                  >
                    {/* top logo and company name */}
                    <div
                      className="testBlu1"
                      style={{
                        width: '100%',
                        height: '112px',
                        fontWeight: 'bolder',
                        display: 'flex',
                        justifyContent: 'start',
                        borderBottom: '1px solid grey',
                        padding: '4px',
                      }}
                    >
                      {/* company logo */}
                      <Avatar
                        shape="square"
                        size={48}
                        src={blurCardObj.avatar}
                        alt="company-logo"
                      />
                      <div
                        style={{
                          marginLeft: '8px',
                          color: 'black',
                          padding: '4px 0px',
                          maxWidth: '100%',
                          maxHeight: '100px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          flexWrap: 'wrap',
                        }}
                        className="testR1"
                      >
                        {/* company name */}
                        <span style={{ marginBottom: '24px' }}>
                          {blurCardObj.sealine}
                        </span>
                        {/* company desc */}
                        <span>{blurCardObj.description}</span>
                      </div>
                    </div>
                    {/* bottom route part */}
                    <div
                      className="testBlu1"
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        height: '85px',
                        padding: '10px',
                        fontSize: '14px',
                        fontWeight: 'bolder',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'hidden',
                        flexWrap: 'wrap',
                        borderBottom: '1px solid grey',
                      }}
                    >
                      {/* origin */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <span
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: this.props.secondaryColor,
                          }}
                        ></span>
                        <span>{blurCardObj.port_of_origin}</span>
                      </div>

                      {/* horz. line */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: '50px',
                        }}
                      >
                        <div
                          style={{
                            maxWidth: '100%',
                            width: '100%',
                            background: this.props.secondaryColor,
                            height: '4px',
                          }}
                        ></div>
                        <span>&nbsp;</span>
                      </div>

                      {/* destination */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <span
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: this.props.secondaryColor,
                          }}
                        ></span>
                        <span>{blurCardObj.port_of_discharge.company_name}</span>
                      </div>

                      {/* horz. line */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: '50px',
                        }}
                      >
                        <div
                          style={{
                            maxWidth: '100%',
                            width: '100%',
                            background: this.props.secondaryColor,
                            height: '4px',
                          }}
                        ></div>
                        <span>&nbsp;</span>
                      </div>

                      {/* delivery */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <span
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: this.props.secondaryColor,
                          }}
                        ></span>
                        <span>{blurCardObj.port_of_discharge.company_name}</span>
                      </div>
                    </div>
                    {/* view-details */}
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        height: '30px',
                        color: 'var(--blueColor)',
                        fontWeight: 'bold',
                      }}
                      className="testO1"
                    >
                      View Details
                    </div>
                  </div>

                  {/* right side of card */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      maxWidth: '224px',
                      width: '100%',
                      border: '1px solid grey',
                      borderLeft: 'none',
                    }}
                    className="testR1"
                  >
                    {/* top company name */}
                    <div
                      className="testBlu1"
                      style={{
                        width: '100%',
                        height: '34px',
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center',
                        fontWeight: 'bolder',
                        borderBottom: '1px solid grey',
                      }}
                    >
                      {/* company logo */}
                      <Avatar
                        shape="square"
                        size={24}
                        src={blurCardObj.avatar}
                        alt="company-logo"
                      />
                      <span>{blurCardObj.sealine}</span>
                    </div>
                    {/* no. of container */}
                    <div
                      className="testBlu1"
                      style={{
                        width: '100%',
                        height: '83px',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '8px',
                        fontWeight: 'bolder',
                        borderBottom: '1px solid grey',
                      }}
                    >
                      <span>20 Standard 1</span>
                      <span>40 Standard 1</span>
                      <span>40 High Cube 1</span>
                    </div>
                    {/* book now btn */}
                    <div
                      className="testBlu1"
                      style={{
                        width: '100%',
                        height: '110px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Button
                        size="large"
                        style={{
                          height: '65px',
                          maxWidth: '130px',
                          padding: '4px 26px 4px 26px',
                          border: 'none',
                          color: '#fff',
                          background: this.props.secondaryColor,
                          borderBottom: `2px solid rgba(128,128,128,0.9)`,
                          fontSize: '16px',
                          borderRadius: '5px',
                        }}
                      >
                        <div>
                          <span style={{ fontSize: '16px', fontWeight: 600 }}>
                            {blurCardObj.bookNowPrice}
                          </span>
                          <div style={{ fontSize: '8px' }}>
                            {blurCardObj.currency}
                          </div>
                        </div>

                        <div>Book Now</div>
                      </Button>
                    </div>
                  </div>
                </div>
              </StyledCard>
            );
          })}

        {/* unlock / hard-gate overlay */}
        {noOfResultBlurCards > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              paddingTop: `${topMargin}px`,
            }}
          >
            <div
              ref={emailFormRef}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                zIndex: 1,
                height: 'fit-content',
              }}
              className="testBlu1"
            >
              <GateCard
                role="dialog"
                aria-label={isGated ? COPY.gateTitle : COPY.unlockTitle}
              >
                {this.renderGatePanel(status, isGated)}
                {G_RECAPTCHA_SITE_KEY && (
                  <Recaptcha
                    ref={(ref) => (this.recaptcha = ref)}
                    sitekey={G_RECAPTCHA_SITE_KEY}
                    onResolved={this.onCaptchaResolved}
                    onLoaded={this.onCaptchaLoaded}
                    style={{
                      visibility: 'hidden',
                      width: '0px',
                      height: '0px',
                    }}
                  />
                )}
              </GateCard>
            </div>
          </div>
        )}
      </div>
    );
  }
}

BlurCards.propTypes = {
  /**
   * Sea FCL rate json
   */
  seaFCLJSON: PropTypes.object,
  /**
   * Sea LCL rate json
   */
  seaLCLJSON: PropTypes.object,
  /**
   * AIR rate json
   */
  airJSON: PropTypes.object,

  /**
   * Primary Color of the web app
   */
  primaryColor: PropTypes.string,

  /**
   * Secondary Color of the web app
   */
  secondaryColor: PropTypes.string,

  /**
   * It's the parent website scrollbar position.
   */
  parentScrollbarPosition: PropTypes.number,

  /**
   * It's value is flipped upon submitting postal codes,
   * so that pickup delivery ers line can re-render and prices will be updated.
   */
  updatePickupDeliveryErsLine: PropTypes.bool,

  /**
   * Email of the user
   */
  userEmailBlurCards: PropTypes.string,

  /**
   * Public rate-gate slice ({ status, errorMessage, leadId, loginUrl })
   */
  rateGate: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    seaFCLJSON: state.route.seaFCLJSON,
    seaLCLJSON: state.route.seaLCLJSON,
    airJSON: state.route.airJSON,

    primaryColor: state.code_config.primaryColor,
    secondaryColor: state.code_config.secondaryColor,

    parentScrollbarPosition: state.code_config.parentScrollbarPosition,
    updatePickupDeliveryErsLine: state.route.updatePickupDeliveryErsLine,
    userEmailBlurCards: state.cache.userEmailBlurCards,
    rateGate: state.route.rateGate,
  };
};

export default connect(mapStateToProps, actions)(BlurCards);
